import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Lock, Loader2, Save, LogOut, Gavel, Home } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Judge() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [cands, setCands] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const loadCands = useCallback(() => api.get("/judge/candidates").then((r) => setCands(r.data)).catch(() => {}), []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["judge", "admin", "super_admin"].includes(user.role)) { navigate("/account"); return; }
    api.get("/judging/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
    loadCands();
  }, [user, loading, navigate, loadCands]);

  if (loading || !user) return <div className="flex min-h-screen items-center justify-center bg-obsidian text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const open = (c) => { setActive(c); setDraft({ ...(c.my_scores?.scores || {}) }); };

  const submit = async (lock) => {
    setBusy(true);
    try {
      await api.post("/judge/scores", { candidate_id: active.id, scores: draft, lock });
      toast.success(lock ? "Scores locked & submitted" : "Scores saved");
      await loadCands();
      if (lock) setActive(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const total = Object.values(draft).reduce((s, v) => s + (Number(v) || 0), 0);

  return (
    <div className="min-h-screen bg-obsidian" data-testid="judge-page">
      <header className="glass-strong sticky top-0 z-40 border-b border-gold/20">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-2"><Gavel className="h-5 w-5 text-gold" /><div><p className="font-serif text-sm text-amber-50 leading-none">Judges' Chamber</p><p className="font-mono text-[0.55rem] tracking-[0.25em] gold-text">CONFIDENTIAL SCORING</p></div></div>
          <div className="flex items-center gap-2">
            <Link to="/" className="rounded-full border border-gold/25 p-2 text-gold hover:bg-gold/10"><Home className="h-4 w-4" /></Link>
            <button onClick={() => { logout(); navigate("/"); }} className="rounded-full border border-gold/25 px-4 py-2 text-sm text-gold inline-flex items-center gap-2"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
        <p className="kicker mb-2">Welcome, {user.name}</p>
        <h1 className="text-3xl text-amber-50">Score the Candidates</h1>
        <p className="mt-2 text-sm text-amber-100/60">Scores are confidential and locked once submitted. Categories are set by headquarters.</p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="judge-candidate-list">
          {cands.map((c) => {
            const locked = c.my_scores?.locked;
            const scored = Object.keys(c.my_scores?.scores || {}).length;
            return (
              <button key={c.id} onClick={() => open(c)} data-testid={`judge-cand-${c.id}`}
                className={`card-lux flex items-center gap-3 p-4 text-left ${locked ? "opacity-70" : ""}`}>
                <img src={c.portrait_url} alt="" className="h-12 w-12 rounded-full object-cover object-top" />
                <div className="min-w-0 flex-1"><p className="truncate text-sm text-amber-50">{c.name} {c.flag}</p><p className="text-xs text-muted-foreground">{scored}/{cats.length} scored</p></div>
                {locked && <Lock className="h-4 w-4 text-gold" />}
              </button>
            );
          })}
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4" onClick={() => setActive(null)}>
          <div className="w-full max-w-lg glass-strong rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="judge-score-modal">
            <div className="mb-4 flex items-center gap-3">
              <img src={active.portrait_url} alt="" className="h-12 w-12 rounded-full object-cover object-top" />
              <div><h2 className="font-serif text-xl text-amber-50">{active.name} {active.flag}</h2><p className="text-xs text-muted-foreground">{active.delegation_name}</p></div>
            </div>
            {active.my_scores?.locked ? (
              <p className="rounded-lg bg-gold/10 px-4 py-3 text-sm text-gold flex items-center gap-2"><Lock className="h-4 w-4" /> Your scores for this candidate are locked.</p>
            ) : (
              <div className="space-y-3">
                {cats.map((cat) => (
                  <div key={cat.key} className="flex items-center gap-3">
                    <span className="flex-1 text-sm text-amber-50">{cat.name}</span>
                    <input type="range" min="0" max="10" step="0.5" value={draft[cat.key] ?? 0}
                      onChange={(e) => setDraft((d) => ({ ...d, [cat.key]: Number(e.target.value) }))}
                      data-testid={`judge-slider-${cat.key}`} className="w-32 accent-[#E6005C]" />
                    <span className="w-8 text-right font-mono text-sm gold-text">{draft[cat.key] ?? 0}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-gold/15 pt-3">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="font-mono text-lg gold-text">{total} / {cats.length * 10}</span>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={() => submit(false)} disabled={busy} className="btn-ghost-gold flex-1 inline-flex items-center justify-center gap-2" data-testid="judge-save"><Save className="h-4 w-4" /> Save</button>
                  <button onClick={() => submit(true)} disabled={busy} className="btn-gold flex-1 inline-flex items-center justify-center gap-2" data-testid="judge-lock"><Lock className="h-4 w-4" /> Lock & Submit</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
